import { createFileRoute, redirect, useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { useAuth } from '@/client/hooks/useAuth';
import { supabase } from '@/client/lib/supabase';

export const Route = createFileRoute('/login')({
  beforeLoad: async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (session) throw redirect({ to: '/' });
  },
  component: LoginPage,
});

function EyeIcon({ open }: { open: boolean }) {
  if (open) {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
        <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
        <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
        <line x1="2" x2="22" y1="2" y2="22" />
      </svg>
    );
  }
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function GaraLogo({ dark = false }: { dark?: boolean }) {
  const bg = dark ? '#fafafa' : '#1e293b';
  const stroke = dark ? '#0f172a' : '#fafafa';
  return (
    <div className="flex items-center gap-2">
      <svg width="26" height="26" viewBox="0 0 28 28" fill="none" aria-hidden>
        <rect x="0.5" y="0.5" width="27" height="27" rx="7" fill={bg} />
        <path
          d="M19.5 11.2c-.7-1.7-2.4-2.9-4.5-2.9-2.8 0-5 2.2-5 5s2.2 5 5 5c2.4 0 4.4-1.7 4.9-4h-4.5"
          stroke={stroke}
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span
        className="text-[18px] font-semibold tracking-tight"
        style={{ color: dark ? '#fafafa' : '#0f172a' }}
      >
        Gara
        <span style={{ opacity: 0.55, fontWeight: 500, marginLeft: 2 }}>Tech</span>
      </span>
    </div>
  );
}

function LoginPage() {
  const router = useRouter();
  const [mode, setMode] = useState<'signin' | 'signup' | 'forgot' | 'forgot-sent'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [keepSignedIn, setKeepSignedIn] = useState(false);
  const { signIn, signUp, sendPasswordResetEmail, loading, error, setError } = useAuth();

  function switchMode(next: 'signin' | 'signup' | 'forgot') {
    setError(null);
    setMode(next);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (mode === 'forgot') {
      const ok = await sendPasswordResetEmail(email);
      if (ok) setMode('forgot-sent');
      return;
    }
    const ok = mode === 'signin' ? await signIn(email, password) : await signUp(email, password);
    if (ok) router.navigate({ to: '/' });
  }

  const headings: Record<typeof mode, { title: string; subtitle: string }> = {
    signin: { title: 'Welcome back', subtitle: 'Sign in to your Gara Tech workspace.' },
    signup: { title: 'Create account', subtitle: 'Get started with Gara Tech.' },
    forgot: { title: 'Reset password', subtitle: "Enter your email and we'll send you a reset link." },
    'forgot-sent': { title: 'Check your inbox', subtitle: `We sent a reset link to ${email}.` },
  };

  return (
    <div className="flex h-screen overflow-hidden bg-white">
      {/* ── Brand panel ── */}
      <div
        className="relative hidden flex-[1.1] flex-col overflow-hidden p-11 text-white lg:flex"
        style={{ background: 'linear-gradient(160deg, #0f172a 0%, #1e293b 60%, #2563eb 200%)' }}
      >
        {/* Grid overlay */}
        <svg className="absolute inset-0 h-full w-full opacity-[0.06]" aria-hidden>
          <defs>
            <pattern id="grid" width="32" height="32" patternUnits="userSpaceOnUse">
              <path d="M32 0H0V32" fill="none" stroke="#fff" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>

        {/* Radial glow */}
        <div
          className="absolute -right-32 -top-28 h-[420px] w-[420px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(37,99,235,0.45), transparent 60%)' }}
          aria-hidden
        />

        <div className="relative">
          <GaraLogo dark />
        </div>

        <div className="relative mt-auto flex flex-col gap-5">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-white/55">
            The Backoffice
          </p>
          <h1 className="m-0 max-w-[480px] text-[44px] font-semibold leading-[1.05] tracking-tight">
            Run the studio,
            <br />
            not the spreadsheets.
          </h1>
          <p className="m-0 max-w-[420px] text-[15px] leading-relaxed text-white/70">
            People, projects, hours and finance — one calm place for the whole team.
          </p>

          <div className="mt-6 flex gap-7">
            {[
              { value: '€482k', label: 'Revenue YTD' },
              { value: '8', label: 'Active projects' },
              { value: '12', label: 'People' },
            ].map((s) => (
              <div key={s.label}>
                <div className="font-variant-numeric text-[22px] font-semibold tabular-nums">
                  {s.value}
                </div>
                <div className="mt-0.5 text-xs text-white/55">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative mt-8 text-xs text-white/45">© 2026 Gara Tech — Paris · Lyon</div>
      </div>

      {/* ── Form panel ── */}
      <div className="flex flex-1 items-center justify-center p-10">
        <div className="flex w-full max-w-[360px] flex-col gap-[22px]">
          {/* Mobile logo */}
          <div className="lg:hidden">
            <GaraLogo />
          </div>

          {/* Heading */}
          <div className="flex flex-col gap-1.5">
            <h1 className="m-0 text-[26px] font-semibold tracking-tight text-slate-900">
              {headings[mode].title}
            </h1>
            <p className="m-0 text-[13px] text-neutral-500">{headings[mode].subtitle}</p>
          </div>

          {/* Google SSO — only for sign-in / sign-up */}
          {(mode === 'signin' || mode === 'signup') && (
            <>
              <button
                type="button"
                className="flex h-[42px] w-full cursor-pointer items-center justify-center gap-2.5 rounded-lg border border-stone-200 bg-white text-[15px] font-medium text-slate-800 transition-colors hover:bg-stone-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
              >
                <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden>
                  <path
                    fill="#4285F4"
                    d="M17.64 9.2c0-.64-.06-1.25-.17-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.71v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.61z"
                  />
                  <path
                    fill="#34A853"
                    d="M9 18c2.43 0 4.47-.81 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.92v2.32A9 9 0 0 0 9 18z"
                  />
                  <path fill="#FBBC05" d="M3.97 10.7a5.4 5.4 0 0 1 0-3.44V4.96H.92a9 9 0 0 0 0 8.07z" />
                  <path
                    fill="#EA4335"
                    d="M9 3.58c1.32 0 2.5.45 3.43 1.35l2.58-2.58A9 9 0 0 0 .92 4.96l3.05 2.3A5.36 5.36 0 0 1 9 3.58z"
                  />
                </svg>
                Continue with Google
              </button>

              <div className="flex items-center gap-2.5">
                <div className="h-px flex-1 bg-stone-200" />
                <span className="text-[11px] uppercase tracking-[0.08em] text-neutral-400">or</span>
                <div className="h-px flex-1 bg-stone-200" />
              </div>
            </>
          )}

          {/* ── Forgot-sent confirmation ── */}
          {mode === 'forgot-sent' && (
            <div className="flex flex-col gap-4">
              <p className="text-[14px] leading-relaxed text-slate-700">
                Follow the link in the email to set a new password. The link expires in 1 hour.
              </p>
              <button
                type="button"
                onClick={() => switchMode('signin')}
                className="self-start cursor-pointer text-[13px] text-blue-600 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-1"
              >
                ← Back to sign in
              </button>
            </div>
          )}

          {/* ── Forgot form ── */}
          {mode === 'forgot' && (
            <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="email" className="text-[13px] font-medium text-slate-800">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="lea@garatech.io"
                  className="h-[42px] w-full rounded-lg border border-stone-200 bg-white px-3 text-[14px] text-slate-900 placeholder:text-neutral-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-600"
                />
              </div>
              {error && (
                <p className="text-[13px] text-red-600" role="alert">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={loading}
                className="mt-0.5 flex h-[42px] w-full cursor-pointer items-center justify-center rounded-lg bg-slate-900 text-[15px] font-medium text-white transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2"
              >
                {loading ? 'Sending…' : 'Send reset link'}
              </button>
              <button
                type="button"
                onClick={() => switchMode('signin')}
                className="cursor-pointer text-center text-[13px] text-blue-600 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-1"
              >
                ← Back to sign in
              </button>
            </form>
          )}

          {/* ── Sign-in / Sign-up form ── */}
          {(mode === 'signin' || mode === 'signup') && (
            <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
              {/* Email */}
              <div className="flex flex-col gap-1.5">
                <label htmlFor="email" className="text-[13px] font-medium text-slate-800">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="lea@garatech.io"
                  className="h-[42px] w-full rounded-lg border border-stone-200 bg-white px-3 text-[14px] text-slate-900 placeholder:text-neutral-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-600"
                />
              </div>

              {/* Password */}
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <label htmlFor="password" className="text-[13px] font-medium text-slate-800">
                    Password
                  </label>
                  {mode === 'signin' && (
                    <button
                      type="button"
                      onClick={() => switchMode('forgot')}
                      className="cursor-pointer text-[12px] text-blue-600 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-1"
                    >
                      Forgot?
                    </button>
                  )}
                </div>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="••••••••"
                    className="h-[42px] w-full rounded-lg border border-stone-200 bg-white px-3 pr-10 text-[14px] text-slate-900 placeholder:text-neutral-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                  <button
                    type="button"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-neutral-400 transition-colors hover:text-neutral-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-1"
                  >
                    <EyeIcon open={showPassword} />
                  </button>
                </div>
              </div>

              {/* Keep signed in */}
              {mode === 'signin' && (
                <div className="mt-0.5 flex items-center gap-2">
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={keepSignedIn}
                    onClick={() => setKeepSignedIn((v) => !v)}
                    className={`flex h-4 w-4 flex-shrink-0 cursor-pointer items-center justify-center rounded-[4px] border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-1 ${
                      keepSignedIn ? 'border-slate-900 bg-slate-900' : 'border-stone-300 bg-white'
                    }`}
                  >
                    {keepSignedIn && (
                      <svg
                        width="10"
                        height="10"
                        viewBox="0 0 12 12"
                        fill="none"
                        stroke="white"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden
                      >
                        <polyline points="2,6 5,9 10,3" />
                      </svg>
                    )}
                  </button>
                  <span
                    className="cursor-default select-none text-[13px] text-slate-700"
                    onClick={() => setKeepSignedIn((v) => !v)}
                  >
                    Keep me signed in for 30 days
                  </span>
                </div>
              )}

              {error && (
                <p className="text-[13px] text-red-600" role="alert">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="mt-0.5 flex h-[42px] w-full cursor-pointer items-center justify-center rounded-lg bg-slate-900 text-[15px] font-medium text-white transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2"
              >
                {loading ? 'Loading…' : mode === 'signin' ? 'Sign in' : 'Sign up'}
              </button>
            </form>
          )}

          {/* Footer — only for signin / signup */}
          {(mode === 'signin' || mode === 'signup') && (
            <p className="text-center text-[13px] text-neutral-500">
              {mode === 'signin' ? (
                <>
                  New to Gara Tech?{' '}
                  <button
                    type="button"
                    onClick={() => switchMode('signup')}
                    className="cursor-pointer font-medium text-blue-600 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-1"
                  >
                    Request access
                  </button>
                </>
              ) : (
                <>
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => switchMode('signin')}
                    className="cursor-pointer font-medium text-blue-600 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-1"
                  >
                    Sign in
                  </button>
                </>
              )}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
